// Chart geometry for the onboarding illustrations, ported from the cornix.io
// redesign (lib/redesign/chart.ts).

import {
  monotonePath,
  smoothThroughSamples,
} from "common/NewLook/monotonePath";

export type Pt = [number, number];

function splineCurvatures(points: Pt[]): number[] {
  const count = points.length;
  const diagonal = new Array<number>(count).fill(1);
  const upper = new Array<number>(count).fill(0);
  const values = new Array<number>(count).fill(0);
  for (let index = 1; index < count - 1; index++) {
    const before = points[index][0] - points[index - 1][0];
    const after = points[index + 1][0] - points[index][0];
    const factor = before / diagonal[index - 1];
    diagonal[index] = 2 * (before + after) - factor * upper[index - 1];
    upper[index] = after;
    values[index] =
      6 *
        ((points[index + 1][1] - points[index][1]) / after -
          (points[index][1] - points[index - 1][1]) / before) -
      factor * values[index - 1];
  }
  for (let index = count - 2; index >= 0; index--)
    values[index] =
      (values[index] - upper[index] * values[index + 1]) / diagonal[index];
  return values;
}

function splineSegments(points: Pt[]): [Pt, Pt, Pt, Pt][] {
  const curvatures = splineCurvatures(points);
  return points.slice(1).map((end, index) => {
    const start = points[index];
    const width = end[0] - start[0];
    const slope = (end[1] - start[1]) / width;
    const entry =
      slope - (width * (2 * curvatures[index] + curvatures[index + 1])) / 6;
    const exit =
      slope + (width * (curvatures[index] + 2 * curvatures[index + 1])) / 6;
    return [
      start,
      [start[0] + width / 3, start[1] + (entry * width) / 3],
      [end[0] - width / 3, end[1] - (exit * width) / 3],
      end,
    ];
  });
}

export function smoothPath(points: Pt[]): string {
  if (!points.length) return "";
  const format = (point: Pt) =>
    point.map((value) => value.toFixed(4)).join(",");
  return (
    `M${format(points[0])}` +
    splineSegments(points)
      .map(
        ([, first, second, end]) =>
          `C${format(first)},${format(second)},${format(end)}`
      )
      .join("")
  );
}

export function linePath(p: Pt[]): string {
  return smoothPath(p);
}

/** Cumulative length fraction (0..1) at each point of a polyline. */
export function lengthFractions(p: Pt[]): {
  fractions: number[];
  total: number;
} {
  const acc = [0];
  for (let i = 1; i < p.length; i++)
    acc.push(
      acc[i - 1] + Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1])
    );
  const total = acc[acc.length - 1] || 1;
  return { fractions: acc.map((a) => a / total), total };
}

/** Every place the polyline crosses horizontal level y, with direction and length-fraction. */
export function crossings(
  p: Pt[],
  y: number
): { x: number; f: number; dir: "up" | "down" }[] {
  const { fractions } = lengthFractions(p);
  const out: { x: number; f: number; dir: "up" | "down" }[] = [];
  for (let i = 1; i < p.length; i++) {
    const [ax, ay] = p[i - 1];
    const [bx, by] = p[i];
    if ((ay - y) * (by - y) < 0) {
      const t = (y - ay) / (by - ay);
      out.push({
        x: ax + (bx - ax) * t,
        f: fractions[i - 1] + (fractions[i] - fractions[i - 1]) * t,
        // SVG y grows downward, so a decreasing y means price is rising.
        dir: by < ay ? "up" : "down",
      });
    }
  }
  return out;
}

/**
 * Dense samples of one continuous curve through the points: no corners at the
 * anchors, and no overshoot past a neighbour's high or low.
 */
// A natural cubic spline: the bend stays continuous across every key (the
// smoothest join; the monotone variant flattens at each key instead).
export function smoothPoints(points: Pt[], steps = 12): Pt[] {
  if (points.length < 2) return [...points];
  const result = splineSegments(points).flatMap(([start, first, second, end]) =>
    Array.from({ length: steps }, (_, index): Pt => {
      const progress = index / steps;
      const remaining = 1 - progress;
      const coordinate = (axis: 0 | 1) =>
        remaining ** 3 * start[axis] +
        3 * remaining ** 2 * progress * first[axis] +
        3 * remaining * progress ** 2 * second[axis] +
        progress ** 3 * end[axis];
      return [coordinate(0), coordinate(1)];
    })
  );
  return [...result, points[points.length - 1]];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Grid rule (as real grid bots run): every line holds one order except the
 * line nearest the price. Lines below hold buys, lines above hold sells. When a
 * buy fills, its sell goes on the next line up; when a sell fills, a buy goes
 * on the next line down. So every sell is one step above its buy.
 */
export function gridTrades(pts: Pt[], levels: number[]) {
  const startY = pts[0][1];
  let empty = levels.reduce(
    (best, y, i) =>
      Math.abs(y - startY) < Math.abs(levels[best] - startY) ? i : best,
    0
  );
  const orders: ("buy" | "sell" | null)[] = levels.map((_, i) =>
    i === empty ? null : i > empty ? "buy" : "sell"
  );
  const all = levels
    .flatMap((y, i) => crossings(pts, y).map((c) => ({ ...c, y, i })))
    .sort((a, b) => a.f - b.f);
  const trades: { x: number; y: number; f: number; side: "buy" | "sell" }[] =
    [];
  for (const c of all) {
    if (c.dir === "down" && orders[c.i] === "buy") {
      trades.push({ x: round2(c.x), y: round2(c.y), f: c.f, side: "buy" });
      orders[c.i] = null;
      orders[c.i - 1] = "sell";
      empty = c.i;
    } else if (c.dir === "up" && orders[c.i] === "sell") {
      trades.push({ x: round2(c.x), y: round2(c.y), f: c.f, side: "sell" });
      orders[c.i] = null;
      orders[c.i + 1] = "buy";
      empty = c.i;
    }
  }
  return trades;
}

/**
 * New-look variants: monotone cubic (Fritsch-Carlson), so a line has no
 * corners and never overshoots a key's highs and lows.
 */
export const monotoneLinePath = (p: Pt[]): string => monotonePath(p);

export const monotonePoints = (points: Pt[], steps = 12): Pt[] =>
  smoothThroughSamples(points, steps);
